import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import type { Tenant, TenantDetail, TenantRole } from '../types/auth'
import type * as SocketCloudOrganizationTypes from '@aaimplatform/socket-cloud-types/generated/socket-cloud-organization.types';

export type { Tenant, TenantDetail, TenantRole };

type OrganizationSchemas = SocketCloudOrganizationTypes.components['schemas'];
// One item of listTenants' TenantListResponse.
export type TenantListItem = OrganizationSchemas['Tenant'];
type TenantListResponse = OrganizationSchemas['TenantListResponse'];
export type TenantListPage = Pick<TenantListResponse, 'total' | 'page' | 'pageSize'>;

export interface TenantFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: OrganizationSchemas['TenantStatus'];
  plan?: OrganizationSchemas['TenantPlan'];
}

export interface UseTenantsResult {
  tenants: TenantListItem[];
  loading: boolean;
  error: string | null;
  pagination: TenantListPage;
  filters: TenantFilters;
  setFilters: (filters: TenantFilters) => void;
  refresh: () => Promise<void>;
  createTenant: (tenantData: {
    name: string;
    slug: string;
    description?: string;
    plan?: string;
    maxUsers?: number;
    maxStorage?: number;
    maxNodes?: number;
    settings?: any;
    features?: string[];
  }) => Promise<boolean>;
  updateTenant: (tenantId: string, updates: {
    name?: string;
    description?: string;
    status?: string;
    plan?: string;
    maxUsers?: number;
    maxStorage?: number;
    maxNodes?: number;
    settings?: any;
    features?: string[];
  }) => Promise<boolean>;
  deleteTenant: (tenantId: string) => Promise<boolean>;
  inviteUser: (tenantId: string, inviteData: {
    email: string;
    roleId: string;
    message?: string;
  }) => Promise<boolean>;
}

export function useTenants(initialFilters: TenantFilters = {}): UseTenantsResult {
  const [tenants, setTenants] = useState<TenantListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<TenantListPage>({
    page: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState<TenantFilters>({
    page: 1,
    pageSize: 20,
    ...initialFilters,
  });

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.getTenants(filters);

      if (response.success && response.data) {
        const { data, total, page, pageSize } = response.data;
        setTenants(data);
        setPagination({ total, page, pageSize });
      } else {
        setError(response.error || 'Failed to fetch tenants');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const refresh = useCallback(() => fetchTenants(), [fetchTenants]);

  const createTenant = useCallback(async (tenantData: {
    name: string;
    slug: string;
    description?: string;
    plan?: string;
    maxUsers?: number;
    maxStorage?: number;
    maxNodes?: number;
    settings?: any;
    features?: string[];
  }): Promise<boolean> => {
    try {
      const response = await apiClient.createTenant(tenantData);
      if (response.success) {
        await fetchTenants(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to create tenant');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant');
      return false;
    }
  }, [fetchTenants]);

  const updateTenant = useCallback(async (tenantId: string, updates: {
    name?: string;
    description?: string;
    status?: string;
    plan?: string;
    maxUsers?: number;
    maxStorage?: number;
    maxNodes?: number;
    settings?: any;
    features?: string[];
  }): Promise<boolean> => {
    try {
      const response = await apiClient.updateTenant(tenantId, updates);
      if (response.success) {
        await fetchTenants(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to update tenant');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant');
      return false;
    }
  }, [fetchTenants]);

  const deleteTenant = useCallback(async (tenantId: string): Promise<boolean> => {
    try {
      const response = await apiClient.deleteTenant(tenantId);
      if (response.success) {
        await fetchTenants(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to delete tenant');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tenant');
      return false;
    }
  }, [fetchTenants]);

  const inviteUser = useCallback(async (tenantId: string, inviteData: {
    email: string;
    roleId: string;
    message?: string;
  }): Promise<boolean> => {
    try {
      const response = await apiClient.inviteUserToTenant(tenantId, inviteData);
      if (response.success) {
        await fetchTenants(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to invite user');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user');
      return false;
    }
  }, [fetchTenants]);

  // Fetch tenants when filters change
  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  return {
    tenants,
    loading,
    error,
    pagination,
    filters,
    setFilters,
    refresh,
    createTenant,
    updateTenant,
    deleteTenant,
    inviteUser,
  };
}

export function useTenant(tenantId: string) {
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTenant = useCallback(async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.getTenant(tenantId);
      
      if (response.success) {
        setTenant(response.data ?? null);
      } else {
        setError(response.error || 'Failed to fetch tenant');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  return {
    tenant,
    loading,
    error,
    refresh: fetchTenant,
  };
}

export function useTenantRoles(tenantId: string) {
  const [roles, setRoles] = useState<TenantRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.getTenantRoles(tenantId);

      if (response.success && response.data) {
        setRoles(response.data.roles);
      } else {
        setError(response.error || 'Failed to fetch tenant roles');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const createRole = useCallback(async (roleData: {
    name: string;
    description: string;
    permissions: string[];
  }): Promise<boolean> => {
    try {
      const response = await apiClient.createTenantRole(tenantId, roleData);
      if (response.success) {
        await fetchRoles(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to create role');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create role');
      return false;
    }
  }, [tenantId, fetchRoles]);

  const updateRole = useCallback(async (roleId: string, updates: {
    name?: string;
    description?: string;
    permissions?: string[];
  }): Promise<boolean> => {
    try {
      const response = await apiClient.updateTenantRole(tenantId, roleId, updates);
      if (response.success) {
        await fetchRoles(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to update role');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
      return false;
    }
  }, [tenantId, fetchRoles]);

  const deleteRole = useCallback(async (roleId: string): Promise<boolean> => {
    try {
      const response = await apiClient.deleteTenantRole(tenantId, roleId);
      if (response.success) {
        await fetchRoles(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to delete role');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role');
      return false;
    }
  }, [tenantId, fetchRoles]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return {
    roles,
    loading,
    error,
    refresh: fetchRoles,
    createRole,
    updateRole,
    deleteRole,
  };
}